"use client"

import * as React from "react"
import { usePage } from "@inertiajs/react"
import { Circle, ChevronDown, Check } from "lucide-react"

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/shadcn/dropdown-menu"
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/shadcn/sidebar"
import { cn } from "@/lib/utils"
import { useActivityTracker } from "@/hooks/useActivityTracker"

type OnlineStatus = "online" | "away" | "offline"

interface StatusConfig {
  label: string
  color: string
  bgColor: string
  description: string
}

const statusConfig: Record<OnlineStatus, StatusConfig> = {
  online: {
    label: "Online",
    color: "text-emerald-500",
    bgColor: "bg-emerald-500",
    description: "Available for chats",
  },
  away: {
    label: "Away",
    color: "text-amber-400",
    bgColor: "bg-amber-400",
    description: "Temporarily unavailable",
  },
  offline: {
    label: "Offline",
    color: "text-gray-400",
    bgColor: "bg-gray-400",
    description: "Not receiving chats",
  },
}

interface PageProps {
  current_user: {
    id: string
    online_status?: OnlineStatus
  } | null
}

export function AvailabilityStatusToggle() {
  const { props: pageProps } = usePage<{ props: PageProps }>()
  const { current_user } = pageProps as unknown as PageProps
  const { isMobile, state } = useSidebar()
  const isCollapsed = state === "collapsed"

  const [status, setStatus] = React.useState<OnlineStatus>(
    (current_user?.online_status as OnlineStatus) || "offline"
  )
  const [isUpdating, setIsUpdating] = React.useState(false)

  // Activity tracker handles heartbeats and auto-away
  useActivityTracker((newStatus) => {
    // Update local state when status changes from auto-away
    setStatus(newStatus)
  })

  const currentConfig = statusConfig[status]

  const handleStatusChange = async (newStatus: OnlineStatus) => {
    if (newStatus === status || isUpdating) return

    setIsUpdating(true)
    const previousStatus = status
    setStatus(newStatus) // Optimistic update

    try {
      const response = await fetch("/profile/status", {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || "",
        },
        body: JSON.stringify({ status: newStatus }),
      })

      if (!response.ok) {
        throw new Error("Failed to update status")
      }
    } catch (error) {
      console.error("Failed to update status:", error)
      setStatus(previousStatus) // Rollback on error
    } finally {
      setIsUpdating(false)
    }
  }

  if (!current_user) return null

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className={cn(
                "data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground cursor-pointer",
                isUpdating && "opacity-70"
              )}
              tooltip={isCollapsed ? `Status: ${currentConfig.label}` : undefined}
            >
              <div className="flex items-center justify-center size-8">
                <div className="relative">
                  <Circle className={cn("size-4 fill-current", currentConfig.color)} />
                  {isUpdating && (
                    <div className="absolute inset-0 animate-ping">
                      <Circle className={cn("size-4 fill-current opacity-50", currentConfig.color)} />
                    </div>
                  )}
                </div>
              </div>
              {!isCollapsed && (
                <>
                  <div className="grid flex-1 text-left text-sm leading-tight">
                    <span className="truncate font-medium">{currentConfig.label}</span>
                    <span className="truncate text-xs text-sidebar-muted-foreground">
                      {currentConfig.description}
                    </span>
                  </div>
                  <ChevronDown className="ml-auto size-4 text-sidebar-muted-foreground" />
                </>
              )}
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
            side={isMobile ? "top" : "right"}
            align="start"
            sideOffset={4}
          >
            {(Object.entries(statusConfig) as [OnlineStatus, StatusConfig][]).map(
              ([statusKey, config]) => (
                <DropdownMenuItem
                  key={statusKey}
                  onSelect={() => handleStatusChange(statusKey)}
                  className="cursor-pointer gap-3 py-2"
                >
                  <Circle className={cn("size-3 fill-current", config.color)} />
                  <div className="flex-1">
                    <div className="font-medium">{config.label}</div>
                    <div className="text-xs text-muted-foreground">{config.description}</div>
                  </div>
                  {status === statusKey && (
                    <Check className="size-4 text-primary" />
                  )}
                </DropdownMenuItem>
              )
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  )
}
